import React, { useState, InputHTMLAttributes, ReactNode, CSSProperties } from 'react';

export type InputSize = 'sm' | 'md' | 'lg';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'style'> & {
  leading?: ReactNode;
  trailing?: ReactNode;
  size?: InputSize;
  invalid?: boolean;
  label?: ReactNode;
  hint?: ReactNode;
  style?: CSSProperties;
  inputStyle?: CSSProperties;
};

const HEIGHT_MAP: Record<InputSize, number> = { sm: 36, md: 44, lg: 52 };

export function Input({
  leading,
  trailing,
  value,
  onChange,
  placeholder,
  type = 'text',
  size = 'md',
  invalid,
  label,
  hint,
  style,
  inputStyle,
  autoFocus,
  ...rest
}: InputProps) {
  const [focus, setFocus] = useState(false);
  const h = HEIGHT_MAP[size];

  return (
    <label style={{ display: 'block', ...style }}>
      {label && (
        <div
          className="t-footnote"
          style={{ color: 'var(--fg-secondary)', marginBottom: 6, fontWeight: 500 }}
        >
          {label}
        </div>
      )}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          height: h, padding: '0 12px',
          borderRadius: 'var(--r-md)',
          background: 'var(--bg-surface)',
          border: `1px solid ${invalid ? 'var(--danger)' : focus ? 'var(--accent)' : 'var(--line-border)'}`,
          boxShadow: 'none',
          transition: 'box-shadow var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out)',
        }}
      >
        {leading && (
          <span style={{ color: 'var(--fg-tertiary)', display: 'inline-flex' }}>{leading}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          style={{
            flex: 1, border: 0, outline: 0, background: 'transparent',
            color: 'var(--fg-primary)',
            fontSize: 16,
            ...inputStyle,
          }}
          {...rest}
        />
        {trailing && (
          <span style={{ color: 'var(--fg-tertiary)', display: 'inline-flex' }}>{trailing}</span>
        )}
      </div>
      {hint && (
        <div
          className="t-footnote"
          style={{ color: invalid ? 'var(--danger)' : 'var(--fg-tertiary)', marginTop: 6 }}
        >
          {hint}
        </div>
      )}
    </label>
  );
}

export default Input;
